import { ADYEN_ADDRESS, YAVRIO_ADDRESS } from "./paymentLinkTerms.constants";

function buildExternalLink(text, address) {
    return Object.freeze({ link: Object.freeze({ text, address, opensInNewTab: true }) });
}

function buildEmailLink(emailAddress) {
    return Object.freeze({ link: Object.freeze({ text: emailAddress, address: "mailto:" + emailAddress, opensInNewTab: false }) });
}

export const PAYMENT_LINK_TERMS_LAST_UPDATED = Object.freeze(["23 September 2026"]);

export const PAYMENT_LINK_TERMS_INTRODUCTION =
    "These terms apply when you pay using a payment link that a business sent you through Yavrio. Please read them before you pay.";

export const PAYMENT_LINK_TERMS_AGREEMENT = "By proceeding with the payment, you agree to these terms.";

export const PAYMENT_LINK_TERMS_SECTIONS = Object.freeze([
    Object.freeze({
        id: "who-you-are-paying",
        heading: "1. Who you are paying",
        clauses: Object.freeze([
            Object.freeze({
                number: "1.1",
                text: Object.freeze([
                    "The business named on the payment page issued the invoice, order or payment request you are paying for. That business is the seller, and your purchase is between you and the seller.",
                ]),
            }),
            Object.freeze({
                number: "1.2",
                text: Object.freeze([
                    "Yavrio provides the technology used to generate and deliver the payment link for the seller. Card payments are collected and processed through Adyen's payment platform. Yavrio, Ltd. is registered in England and Wales under company number 13983846, at 86-90 Paul Street, London, England, EC2A 4NE.",
                ]),
            }),
            Object.freeze({
                number: "1.3",
                text: Object.freeze([
                    "Adyen N.V. is Yavrio's payment service provider. Adyen handles your card details and authorises the payment. Neither Yavrio nor the seller sees your full card number.",
                ]),
            }),
        ]),
    }),
    Object.freeze({
        id: "your-purchase",
        heading: "2. Your purchase",
        clauses: Object.freeze([
            Object.freeze({
                number: "2.1",
                text: Object.freeze([
                    "The goods or services you are paying for, how and when you receive them, and where they can be delivered are set by the seller's own terms. The seller gave you those terms when you agreed the purchase.",
                ]),
            }),
            Object.freeze({
                number: "2.2",
                text: Object.freeze([
                    "Refunds, returns and cancellations are also governed by the seller's terms. To ask for a refund, return goods or cancel a service, contact the seller.",
                ]),
            }),
            Object.freeze({
                number: "2.3",
                text: Object.freeze([
                    "Yavrio is not a party to the purchase agreement between you and the seller and, to the fullest extent permitted by law, is not liable for any goods, services, refunds, delivery failures, cancellations or disputes relating to the seller.",
                ]),
            }),
        ]),
    }),
    Object.freeze({
        id: "how-the-payment-works",
        heading: "3. How the payment works",
        clauses: Object.freeze([
            Object.freeze({
                number: "3.1",
                text: Object.freeze([
                    "The payment page shows the amount, the currency and a reference for what you are paying. You make one payment of that amount when you confirm it on the page.",
                ]),
            }),
            Object.freeze({
                number: "3.2",
                text: Object.freeze(["Refunds are normally returned to the card used for the original payment."]),
            }),
            Object.freeze({
                number: "3.3",
                text: Object.freeze([
                    "The payment will appear on your statement using the descriptor determined by the seller's payment setup, which may include the seller's trading name.",
                ]),
            }),
            Object.freeze({
                number: "3.4",
                text: Object.freeze([
                    "Each payment link can only be used once, and it stops working at the expiry time the seller set. After a successful payment, or once the link has expired or been cancelled, it cannot be used again.",
                ]),
            }),
            Object.freeze({
                number: "3.5",
                text: Object.freeze([
                    "Payments are processed by Adyen. Yavrio does not receive, hold or safeguard customer funds and does not operate a payment account for you.",
                ]),
            }),
            Object.freeze({
                number: "3.6",
                text: Object.freeze(["A payment is not completed until the payment page confirms success."]),
            }),
            Object.freeze({
                number: "3.7",
                text: Object.freeze([
                    "Yavrio, Adyen or the seller may refuse, cancel or delay a payment where required for security, fraud prevention, legal or regulatory reasons.",
                ]),
            }),
        ]),
    }),
    Object.freeze({
        id: "saving-your-card",
        heading: "4. Saving your card for future payments",
        clauses: Object.freeze([
            Object.freeze({
                number: "4.1",
                text: Object.freeze([
                    "The payment page may offer to save your card. Your card is only saved if you tick the box to agree.",
                ]),
            }),
            Object.freeze({
                number: "4.2",
                text: Object.freeze([
                    "If you agree, Adyen stores your card details securely. The seller may use the saved payment method for future payments that you have authorised under your agreement with the seller and in accordance with applicable card scheme requirements.",
                ]),
            }),
            Object.freeze({
                number: "4.3",
                text: Object.freeze([
                    "Each future payment will be for an amount you owe the seller under your agreement with them. The seller will tell you about the payment in line with that agreement.",
                ]),
            }),
            Object.freeze({
                number: "4.4",
                text: Object.freeze([
                    "You can withdraw your agreement at any time, and the seller will then stop using the saved card. To withdraw your agreement, contact the seller. If you contact Yavrio, we may refer your request to the seller for action.",
                ]),
            }),
        ]),
    }),
    Object.freeze({
        id: "problems-with-a-payment",
        heading: "5. Problems with a payment",
        clauses: Object.freeze([
            Object.freeze({
                number: "5.1",
                text: Object.freeze([
                    "If you have a question about the purchase itself, such as the amount owed, what you received or a refund, contact the seller.",
                ]),
            }),
            Object.freeze({
                number: "5.2",
                text: Object.freeze([
                    "If you have a problem with the payment page or the payment itself, contact Yavrio at ",
                    buildEmailLink(YAVRIO_ADDRESS.SUPPORT_EMAIL),
                    ".",
                ]),
            }),
            Object.freeze({
                number: "5.3",
                text: Object.freeze([
                    "If you do not recognise a payment, please contact the seller. You may also contact Yavrio if you believe there has been a technical issue with the payment process.",
                ]),
            }),
            Object.freeze({
                number: "5.4",
                text: Object.freeze([
                    "To the fullest extent permitted by law, Yavrio shall not be liable for any loss arising from the underlying purchase transaction, any act or omission of the seller, or any interruption, failure or unavailability of the payment link service. Where Yavrio is found liable in connection with the payment link service, Yavrio's total liability shall not exceed the amount of the payment made through the relevant payment link. Nothing in these terms excludes or limits any liability that cannot legally be excluded or limited.",
                ]),
            }),
        ]),
    }),
    Object.freeze({
        id: "your-personal-data",
        heading: "6. Your personal data",
        clauses: Object.freeze([
            Object.freeze({
                number: "6.1",
                text: Object.freeze([
                    "Payment information (including your card details, name and any billing information) entered on the payment page is collected and processed by Adyen. Yavrio does not receive or store your full card details. We process the payment reference and the result of the payment.",
                ]),
            }),
            Object.freeze({
                number: "6.2",
                text: Object.freeze([
                    "Yavrio shares the result of the payment with the seller so they can match it to what you owe them.",
                ]),
            }),
            Object.freeze({
                number: "6.3",
                text: Object.freeze([
                    "Adyen acts as an independent data controller for payment processing, fraud monitoring and its regulatory checks.",
                ]),
            }),
            Object.freeze({
                number: "6.4",
                text: Object.freeze([
                    "Our ",
                    buildExternalLink("privacy policy", YAVRIO_ADDRESS.PRIVACY_POLICY),
                    " explains how we use your data, how long we keep it, how we use cookies, and your rights. The seller's own privacy notice covers how the seller uses your data. Information about how Adyen processes personal data is available in ",
                    buildExternalLink("Adyen's privacy policy", ADYEN_ADDRESS.PRIVACY_POLICY),
                    ".",
                ]),
            }),
        ]),
    }),
    Object.freeze({
        id: "governing-law",
        heading: "7. Governing law",
        clauses: Object.freeze([
            Object.freeze({
                text: Object.freeze([
                    "These terms are governed by the laws of England and Wales. The courts of England and Wales shall have non-exclusive jurisdiction. Nothing in these terms affects any rights you may have under the mandatory laws of the country where you live. These terms apply only to your use of Yavrio's payment link service and do not affect the law that applies to your purchase from the seller.",
                ]),
            }),
        ]),
    }),
]);

export const PAYMENT_LINK_TERMS_CONTACT = Object.freeze({
    id: "contact-us",
    heading: "8. Contact us",
    companyName: "Yavrio, Ltd.",
    postalAddress: "86-90 Paul Street, London, England, EC2A 4NE",
    emailLink: buildEmailLink(YAVRIO_ADDRESS.SUPPORT_EMAIL),
});
